/*
  PeopleGenerationMethod.jsx
*/

// Npm.
import React, { useState } from 'react';

// Components.
import Dialog from '../../../Dialog';
import Selector from '../../../Selector';
import Button from '../../../Button';
import Grid from '../../../Grid';
import TextFieldOnBlur from '../../../TextFieldOnBlur';
import AgeField from './AgeField';
import TimepointsField from './TimepointsField';

// Makes.
import { makeStyles } from '../../../../makes/makeStyles';

// Constants.
import { DEFAULT_NODE_COLOR } from '../../../../utils/constants';

// Helper to get an option's style for the node selectors.
function getOptionStyle(node) {
  return { color: node._botech?.color || DEFAULT_NODE_COLOR };
}

// Styles.
const useStyles = makeStyles(theme => ({
  root: {
    
  },
  infoTable: {
    borderCollapse: 'collapse',
    color: theme.color_text_secondary,
    width: '100%',
  },
  fieldContainer: {
    margin: theme.space(2, 0),
  },
  actionsContainer: {
    textAlign: 'right',
  },
}));

// Helper to get default parameters given a method.
function getDefaultParameters(method) {
  const parameters = {};

  switch (method) {
    case 'single_value':
      parameters.value = 0.1;
      break;
    case 'linear':
      parameters.start_value = 0.1;
      parameters.end_value = 0.2;
      break;
    case 'linear_with_arbitrary_length':
      parameters.start_value = 0.1;
      parameters.end_value = 0.2;
      parameters.start_index = 0;
      parameters.end_index = 2;
      parameters.fill_before = 0.1;
      parameters.fill_after = 0.2;
      break;
    case 'random_from_list':
      parameters.values = [0.1, 0.2, 0.3, 0.4, 0.5];
      break;
    case 'random_from_distribution':
      parameters.distribution_type = 'normal';
      parameters.mean = 0.1;
      parameters.stddev = 10;
      break;
    case 'subset':
      parameters.slice_indices = [[0, 2], [0, 100]];
      parameters.method = 'single_value';
      parameters.parameters = { value: 0.1 };
      parameters.fill_value = 0;
      break;
    case 'n_kwargs':
      parameters.param1 = 0.1;
      parameters.param2 = 0.2;
      parameters.param3 = 0.3;
      break;
  }

  return parameters;
}

/**
 * Form for editing people generation.
 */
export default function PeopleGenerationMethod({
  className,
  label,
  value,
  onChange,
  nodes,
  hasComplement = false,
  disabled = false,
  readOnly = false,
}) {

  nodes = nodes || [];

  const classes = useStyles();
  const [open, setOpen] = useState(false);

  function handleChangeMethod(event) {
    if (onChange) {
      const method = event.target.value;
      onChange({ method, parameters: getDefaultParameters(method) });
    }
  }

  function handleOpen() {
    setOpen(true);
  }

  function handleClose() {
    setOpen(false);
  }

  const useValue = value || {};
  const method = useValue.method || '';
  const parameters = useValue.parameters || {};

  function changeParameters(fields) {
    if (fields && onChange) {
      onChange({ ...value, parameters: { ...parameters, ...fields } });
    }
  }

  return (
    <>

      {
        !disabled &&
        <Dialog
          title={label}
          align="top"
          fullWidth
          open={open}
          onClose={handleClose}
        >

          <div className={classes.fieldContainer}>
            <Selector
              label="Method"
              value={method}
              onChange={handleChangeMethod}
              fullWidth
              disabled={disabled}
              readOnly={readOnly}
            >
              <option value="">None</option>
              <option value="single_value">Single Value</option>
              <option value="linear">Linear</option>
              <option value="linear_with_arbitrary_length">Linear with Arbitrary Length</option>
              <option value="random_from_list">Random from List</option>
              <option value="random_from_distribution">Random from Distribution</option>
              <option value="subset">Subset</option>
              <option value="n_kwargs">N-kwargs</option>
            </Selector>
          </div>

          {
            method === 'single_value' &&
            <div className={classes.fieldContainer}>
              <TextFieldOnBlur
                label="Value"
                value={parameters.value || ''}
                onChange={(val) => changeParameters({ value: Number(val) || 0 })}
                type="number"
                placeholder="0"
                fullWidth
                disabled={disabled}
                readOnly={readOnly}
              />
            </div>
          }

          {
            method === 'linear' &&
            <>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="Start Value"
                  value={parameters.start_value || ''}
                  onChange={(val) => changeParameters({ start_value: Number(val) || 0 })}
                  type="number"
                  placeholder="0"
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="End Value"
                  value={parameters.end_value || ''}
                  onChange={(val) => changeParameters({ end_value: Number(val) || 0 })}
                  type="number"
                  placeholder="0"
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
            </>
          }

          {
            method === 'linear_with_arbitrary_length' &&
            <>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="Start Value"
                  value={parameters.start_value || ''}
                  onChange={(val) => changeParameters({ start_value: Number(val) || 0 })}
                  type="number"
                  placeholder="0"
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="End Value"
                  value={parameters.end_value || ''}
                  onChange={(val) => changeParameters({ end_value: Number(val) || 0 })}
                  type="number"
                  placeholder="0"
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="Start Index"
                  value={parameters.start_index || ''}
                  onChange={(val) => changeParameters({ start_index: Number(val) || 0 })}
                  type="number"
                  placeholder="0"
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="End Index"
                  value={parameters.end_index || ''}
                  onChange={(val) => changeParameters({ end_index: Number(val) || 0 })}
                  type="number"
                  placeholder="0"
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="Fill Before"
                  value={parameters.fill_before || ''}
                  onChange={(val) => changeParameters({ fill_before: Number(val) || 0 })}
                  type="number"
                  placeholder="0"
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="Fill After"
                  value={parameters.fill_after || ''}
                  onChange={(val) => changeParameters({ fill_after: Number(val) || 0 })}
                  type="number"
                  placeholder="0"
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
            </>
          }

          {
            method === 'random_from_list' &&
            <div className={classes.fieldContainer}>
              <TextFieldOnBlur
                label="Values"
                value={parameters.values || ''}
                onChange={(val) => changeParameters({ values: val.split(',').map(Number) })}
                fullWidth
                disabled={disabled}
                readOnly={readOnly}
              />
            </div>
          }

          {
            method === 'random_from_distribution' &&
            <>
              <div className={classes.fieldContainer}>
                <Selector
                  label="Distribution Type"
                  value={parameters.distribution_type}
                  onChange={(event) => changeParameters({ distribution_type: event.target.value })}
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                >
                  <option value="normal">Normal</option>
                  <option value="uniform">Uniform</option>
                  <option value="poisson">Poisson</option>
                  <option value="exponential">Exponential</option>
                </Selector>
              </div>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="Mean"
                  value={parameters.mean || ''}
                  onChange={(val) => changeParameters({ mean: Number(val) || 0 })}
                  type="number"
                  placeholder="0"
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="Standard Deviation"
                  value={parameters.stddev || ''}
                  onChange={(val) => changeParameters({ stddev: Number(val) || 0 })}
                  type="number"
                  placeholder="0"
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
            </>
          }

          {
            method === 'subset' &&
            <>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="Slice Indices"
                  value={parameters.slice_indices || ''}
                  onChange={(val) => changeParameters({ slice_indices: JSON.parse(val) })}
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
              <div className={classes.fieldContainer}>
                <Selector
                  label="Method"
                  value={parameters.method}
                  onChange={(event) => changeParameters({ method: event.target.value })}
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                >
                  <option value="single_value">Single Value</option>
                  <option value="linear">Linear</option>
                  <option value="linear_with_arbitrary_length">Linear with Arbitrary Length</option>
                  <option value="random_from_list">Random from List</option>
                  <option value="random_from_distribution">Random from Distribution</option>
                  <option value="subset">Subset</option>
                  <option value="n_kwargs">N-kwargs</option>
                </Selector>
              </div>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="Parameters"
                  value={JSON.stringify(parameters.parameters)}
                  onChange={(val) => changeParameters({ parameters: JSON.parse(val) })}
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="Fill Value"
                  value={parameters.fill_value || ''}
                  onChange={(val) => changeParameters({ fill_value: Number(val) || 0 })}
                  type="number"
                  placeholder="0"
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
            </>
          }

          {
            method === 'n_kwargs' &&
            <>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="Param 1"
                  value={parameters.param1 || ''}
                  onChange={(val) => changeParameters({ param1: Number(val) || 0 })}
                  type="number"
                  placeholder="0"
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="Param 2"
                  value={parameters.param2 || ''}
                  onChange={(val) => changeParameters({ param2: Number(val) || 0 })}
                  type="number"
                  placeholder="0"
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
              <div className={classes.fieldContainer}>
                <TextFieldOnBlur
                  label="Param 3"
                  value={parameters.param3 || ''}
                  onChange={(val) => changeParameters({ param3: Number(val) || 0 })}
                  type="number"
                  placeholder="0"
                  fullWidth
                  disabled={disabled}
                  readOnly={readOnly}
                />
              </div>
            </>
          }

          <div className={classes.actionsContainer}>
            <Button
              onClick={handleClose}
              disabled={disabled}
            >
              Close
            </Button>
          </div>

        </Dialog>
      }

      <table className={`${classes.infoTable} body2`}>
        <tbody>
          {/*TODO: Make look less crap
          <tr>
            <td>Method:</td>
            <td>{ method }</td>
          </tr>*/}
          <tr>
            <td colSpan="2">
              <Button
                size="small"
                onClick={handleOpen}
                disabled={disabled}
              >
                { readOnly ? 'View' : 'Edit' } ({ method || 'none' })
              </Button>
            </td>
          </tr>
        </tbody>
      </table>

    </>
  );
}
